import { useCallback } from "react";
import { toast } from "@jsq/ds/core";
import {
  AddressType,
  ComplianceObjectType,
  RevertProfileRelationshipType,
} from "~/graphql/supergraphEnums";
import {
  useOverrideRiskRatingMutation,
  useRevertDocumentDifferencesMutation,
  useRevertProfileRelationshipDifferencesMutation,
  useUpdateIndividualProfileMutation,
  useUpdateInstitutionProfileMutation,
} from "~/graphql/supergraphHooks";
import { toISOFormat } from "~/utils/datetime";
import { FieldKey } from "./constants";
import type { ComparisonRowData } from "./gridConfig";
import type { ProfileData } from "./types";
import type {
  BaseDocumentType,
  ComplianceInstitutionProfileInput,
} from "~/graphql/supergraphTypes";

type UseDeleteRowParams = {
  currentProfile: ProfileData | null | undefined;
  proposedProfile: ProfileData | null | undefined;
  proposedProfileId: number;
  caseId: number;
  programId?: number;
};

const mapAddressesToInput = (addresses: ProfileData["addresses"]) => {
  if (!addresses || addresses.length === 0) return [];

  return addresses.map((addr) => ({
    addressType: AddressType.Unknown,
    street: addr.street,
    street2: addr.street2,
    city: addr.city,
    state: addr.state?.code || addr.province,
    postalCode: addr.postalCode,
    country: addr.country?.alpha2code,
  }));
};

export const useDeleteRow = ({
  currentProfile,
  proposedProfile,
  proposedProfileId,
  caseId,
  programId,
}: UseDeleteRowParams) => {
  const [updateIndividualProfile] = useUpdateIndividualProfileMutation({
    onError: (e) => toast.error(e.message),
    refetchQueries: ["GetProfileForComparison"],
    awaitRefetchQueries: true,
  });

  const [updateInstitutionProfile, { loading: isInstitutionUpdating }] =
    useUpdateInstitutionProfileMutation({
      onError: (e) => toast.error(e.message),
      refetchQueries: ["GetProfileForComparison"],
      awaitRefetchQueries: true,
    });

  const [overrideRiskRating] = useOverrideRiskRatingMutation({
    onError: (e) => toast.error(e.message),
    refetchQueries: ["GetProfileForComparison"],
    awaitRefetchQueries: true,
  });

  const [revertProfileRelationshipDifferences] =
    useRevertProfileRelationshipDifferencesMutation({
      onError: (e) => toast.error(e.message),
      refetchQueries: ["GetProfileForComparison"],
      awaitRefetchQueries: true,
    });

  const [revertDocumentDifferences] = useRevertDocumentDifferencesMutation({
    onError: (e) => toast.error(e.message),
    refetchQueries: ["GetProfileForComparison"],
    awaitRefetchQueries: true,
  });

  const handleDeleteRow = useCallback(
    async (row: ComparisonRowData) => {
      if (!currentProfile || !proposedProfile) return;

      const isIndividual =
        currentProfile.complianceObjectType === ComplianceObjectType.Individual;

      try {
        if (row.fieldKey === FieldKey.RiskRating) {
          if (currentProfile.riskRating?.overriddenRating && programId) {
            await overrideRiskRating({
              variables: {
                data: {
                  profileId: proposedProfileId,
                  programId,
                  overriddenRating: currentProfile.riskRating.overriddenRating,
                },
              },
            });
          }
        } else if (row.fieldKey === FieldKey.Owners || row.fieldKey === FieldKey.Controllers) {
          await revertProfileRelationshipDifferences({
            variables: {
              currentProfileId: currentProfile.id,
              proposedProfileId,
              caseId,
              profileType:
                row.fieldKey === FieldKey.Owners
                  ? RevertProfileRelationshipType.Owner
                  : RevertProfileRelationshipType.Controller,
            },
          });
        } else if (row.fieldKey === FieldKey.Document) {
          await revertDocumentDifferences({
            variables: {
              currentProfileId: currentProfile.id,
              proposedProfileId,
              caseId,
              documentType:
                (row.proposedDocs?.[0]?.documentPurpose?.baseType as BaseDocumentType) ??
                (row.currentDocs?.[0]?.documentPurpose?.baseType as BaseDocumentType),
            },
          });
        } else if (isIndividual) {
          const baseData = {
            firstName: proposedProfile.firstName || "",
            lastName: proposedProfile.lastName || "",
            middleName: proposedProfile.middleName,
            addresses: mapAddressesToInput(proposedProfile.addresses),
            dateOfBirth: proposedProfile.dateOfBirth
              ? toISOFormat(proposedProfile.dateOfBirth)
              : undefined,
            taxId: proposedProfile.taxIdUnencrypted,
            taxIdType: proposedProfile.taxIdType,
            caseId,
          };

          const fieldOverride = getIndividualFieldOverride(row.fieldKey, currentProfile);

          await updateIndividualProfile({
            variables: {
              data: { ...baseData, ...fieldOverride },
              profileId: proposedProfileId,
            },
          });
        } else {
          const baseData = {
            legalBusinessName: proposedProfile.legalName || "",
            addresses: mapAddressesToInput(proposedProfile.addresses),
            dateOfFormation: proposedProfile.dateOfOrganization
              ? toISOFormat(proposedProfile.dateOfOrganization)
              : undefined,
            taxId: proposedProfile.taxIdUnencrypted,
            taxIdType: proposedProfile.taxIdType,
            accountType:
              proposedProfile.accountType as ComplianceInstitutionProfileInput["accountType"],
            amlEntityId: proposedProfile.amlEntityTypeId,
            caseId,
          };

          const fieldOverride = getInstitutionFieldOverride(row.fieldKey, currentProfile);

          await updateInstitutionProfile({
            variables: {
              data: { ...baseData, ...fieldOverride },
              profileId: proposedProfileId,
            },
          });
        }

        toast.success("Change reverted successfully");
      } catch {
        // Error is handled by onError callback
      }
    },
    [
      currentProfile,
      proposedProfile,
      proposedProfileId,
      caseId,
      programId,
      updateIndividualProfile,
      updateInstitutionProfile,
      overrideRiskRating,
      revertProfileRelationshipDifferences,
      revertDocumentDifferences,
    ],
  );

  return { deleteRow: handleDeleteRow, isDeleting: isInstitutionUpdating };
};

const getIndividualFieldOverride = (fieldKey: FieldKey, currentProfile: ProfileData) => {
  switch (fieldKey) {
    case FieldKey.LegalName:
      return {
        firstName: currentProfile.firstName || "",
        lastName: currentProfile.lastName || "",
        middleName: currentProfile.middleName,
      };
    case FieldKey.DateOfBirth:
      return {
        dateOfBirth: currentProfile.dateOfBirth
          ? toISOFormat(currentProfile.dateOfBirth)
          : undefined,
      };
    case FieldKey.TaxId:
      return { taxId: currentProfile.taxIdUnencrypted || "" };
    case FieldKey.TaxIdType:
      return { taxIdType: currentProfile.taxIdType };
    case FieldKey.Address:
      return { addresses: mapAddressesToInput(currentProfile.addresses) };
    default:
      return {};
  }
};

const getInstitutionFieldOverride = (fieldKey: FieldKey, currentProfile: ProfileData) => {
  switch (fieldKey) {
    case FieldKey.LegalName:
      return { legalBusinessName: currentProfile.legalName || "" };
    case FieldKey.AccountType:
      return {
        accountType:
          currentProfile.accountType as ComplianceInstitutionProfileInput["accountType"],
      };
    case FieldKey.DateOfOrganization:
      return {
        dateOfFormation: currentProfile.dateOfOrganization
          ? toISOFormat(currentProfile.dateOfOrganization)
          : undefined,
      };
    case FieldKey.TaxId:
      return { taxId: currentProfile.taxIdUnencrypted || "" };
    case FieldKey.TaxIdType:
      return { taxIdType: currentProfile.taxIdType };
    case FieldKey.Address:
      return { addresses: mapAddressesToInput(currentProfile.addresses) };
    case FieldKey.AmlQuestion:
      return { amlEntityId: currentProfile.amlEntityTypeId ?? -1 };
    default:
      return {};
  }
};
